import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, MapPin, Briefcase, ExternalLink, Loader2, ArrowLeft, Zap, Tag, DollarSign, TrendingUp, Sparkles } from 'lucide-react';
import { logger } from '../utils/logger';
import { API } from '../utils/apiBase';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../utils/analytics';

// ─────────────────────────────────────────────────────────────────────────────
// Match pill — color-coded score chip rendered top-right of each card.
// Tooltip on hover shows top matched + top missing keywords for the card so
// users see WHY a job ranks high/low against their resume.
// ─────────────────────────────────────────────────────────────────────────────
const matchTone = (score) => {
  if (score >= 80) return { bg: '#10b981', text: '#fff', label: 'Strong match' };
  if (score >= 60) return { bg: '#002fa7', text: '#fff', label: 'Good match' };
  if (score >= 40) return { bg: '#f59e0b', text: '#0f1115', label: 'Partial match' };
  return { bg: '#dc2626', text: '#fff', label: 'Low match' };
};

const MatchPill = ({ match }) => {
  if (!match) return null;
  const tone = matchTone(match.score);
  const tooltipParts = [];
  if (match.matched?.length) tooltipParts.push(`Matched: ${match.matched.slice(0, 3).join(', ')}`);
  if (match.missing?.length) tooltipParts.push(`Missing: ${match.missing.slice(0, 3).join(', ')}`);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold tracking-wider uppercase flex-shrink-0"
      style={{ background: tone.bg, color: tone.text }}
      title={tooltipParts.join(' · ') || tone.label}
      data-testid={`match-pill-${match.score}`}
    >
      <TrendingUp size={11} /> {match.score}% Match
    </span>
  );
};

const JobCard = ({ job, onTailor, match }) => (
  <div className="dashboard-card p-5 hover:border-[#002fa7] border-2 border-transparent transition-colors" data-testid={`job-card-${job.title.slice(0, 20).replace(/\s/g, '-')}`}>
    <div className="flex items-start gap-3">
      {job.company_logo && (
        <img src={job.company_logo} alt={`${job.company || 'Company'} logo`} className="w-10 h-10 object-contain flex-shrink-0 bg-[#f4f5f7] p-1" onError={(e) => { e.target.style.display = 'none'; }} />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[#f4f5f7] text-[#6b7280]">
              {job.source}
            </span>
            {job.remote && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[#7c3aed]/10 text-[#7c3aed]">
                Remote
              </span>
            )}
            {job.employment_type && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[#002fa7]/10 text-[#002fa7]">
                {job.employment_type.replace('_', ' ')}
              </span>
            )}
          </div>
          <MatchPill match={match} />
        </div>
        <h3 className="font-satoshi text-base font-bold text-[#0f1115]">{job.title}</h3>
        <div className="flex items-center gap-3 mt-1 text-sm text-[#6b7280] flex-wrap">
          <span className="flex items-center gap-1"><Briefcase size={13} /> {job.company}</span>
          {job.location && <span className="flex items-center gap-1"><MapPin size={13} /> {job.location}</span>}
          {job.salary && <span className="flex items-center gap-1 text-[#10b981] font-semibold"><DollarSign size={13} /> {typeof job.salary === 'number' ? `$${job.salary.toLocaleString()}` : job.salary}</span>}
        </div>
        {job.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {job.tags.slice(0, 5).map((tag) => (
              <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-[#f4f5f7] text-[#4b5563] font-medium">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>

    <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#e5e7eb]">
      <button
        onClick={() => onTailor(job, match)}
        className="btn-primary text-xs py-2 px-3 flex items-center gap-1.5"
        data-testid="tailor-resume-btn"
      >
        <Zap size={13} /> Tailor Resume
      </button>
      {job.url && (
        <a
          href={job.url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-outline text-xs py-2 px-3 flex items-center gap-1.5"
          data-testid="view-job-btn"
        >
          <ExternalLink size={13} /> View Job
        </a>
      )}
    </div>
  </div>
);

// Stable identifier for a job — URL is the most reliable, fall back to
// title+company+source so the matching map still works even if URL is null.
const keyFor = (job, idx) => job.url || `${job.source}|${job.title}|${job.company}|${idx}`;

export default function JobBoard() {
  const navigate = useNavigate();
  const apiRef = useRef(API);
  const { isAuthenticated } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [workType, setWorkType] = useState('all');
  const [zipCode, setZipCode] = useState('');
  const [distance, setDistance] = useState('');
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [searched, setSearched] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchError, setSearchError] = useState('');
  const [broadenedInfo, setBroadenedInfo] = useState(null); // { original, used } when JSearch fallback kicked in
  const [semanticInfo, setSemanticInfo] = useState(null);   // { variants, sources, adzunaUsed } when semantic expansion produced extra results
  const [locationRelaxedInfo, setLocationRelaxedInfo] = useState(null); // { original_location } when ZIP/location was dropped to find any results

  // Match scoring state
  const [matches, setMatches] = useState({});            // { job_id: {score, matched, missing} }
  const [matchMeta, setMatchMeta] = useState(null);      // { resume_id, resume_title, reason? }
  const [sortBy, setSortBy] = useState('newest');         // 'newest' | 'best-match'

  // Fetch match scores for the current job batch. Logged-out users get
  // a 401 — we silently skip and show the signin CTA instead.
  const fetchMatches = useCallback(async (jobList) => {
    if (!isAuthenticated || !jobList?.length) return;
    try {
      const payload = {
        jobs: jobList.slice(0, 50).map((j, i) => ({
          job_id: keyFor(j, i),
          title: j.title || '',
          description: j.description || '',
          tags: j.tags || [],
        })),
      };
      const res = await axios.post(`${apiRef.current}/jobs/match-batch`, payload, { withCredentials: true });
      const map = {};
      (res.data?.matches || []).forEach((m) => { map[m.job_id] = m; });
      setMatches(map);
      setMatchMeta({
        resume_id: res.data?.resume_id || null,
        resume_title: res.data?.resume_title || null,
        reason: res.data?.reason || null,
      });
      // Fire analytics: how many cards got scored + average score
      const scores = Object.values(map).map((m) => m.score);
      if (scores.length) {
        const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
        try { trackEvent('job_match_shown', { count: scores.length, avg_score: avg }); } catch (_e) { /* noop */ }
        // Default to best-match sort once scores are available so the user
        // immediately sees the ranking value (research-backed: surfacing the
        // sort lifts engagement vs leaving it on Newest).
        setSortBy('best-match');
      }
    } catch (err) {
      // 401 → user logged out mid-search; silently fall back to no scores.
      // Other errors: log + render cards without pills (graceful degrade).
      logger.error('Job match batch failed:', err?.response?.status || err?.message);
      setMatches({});
      setMatchMeta(null);
    }
  }, [isAuthenticated]);

  const handleSearch = useCallback(async (query) => {
    const q = query !== undefined ? query : searchTerm;
    setLoading(true);
    setSearched(true);
    setSearchError('');
    setBroadenedInfo(null);
    setSemanticInfo(null);
    setLocationRelaxedInfo(null);
    setMatches({});  // reset old scores while we fetch
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (workType !== 'all') params.set('work_type', workType);
      if (zipCode.trim()) params.set('location', zipCode.trim());
      if (distance) params.set('radius', distance);
      if (salaryMin) params.set('salary_min', salaryMin);
      if (salaryMax) params.set('salary_max', salaryMax);
      const res = await axios.get(`${apiRef.current}/jobs/search?${params}`, { withCredentials: true });
      const fetched = res.data.jobs || [];
      setJobs(fetched);
      setTotal(res.data.total || 0);
      if (res.data.broadened && res.data.original_query && res.data.query_used && res.data.original_query !== res.data.query_used) {
        setBroadenedInfo({ original: res.data.original_query, used: res.data.query_used });
      }
      // Semantic expansion notice — only show when expansion actually used
      // more than just the original query AND we got results.
      const variants = res.data.expansion_variants || [];
      if (variants.length > 1 && fetched.length > 0) {
        setSemanticInfo({
          variants,
          sources: res.data.sources || {},
          adzunaUsed: !!res.data.adzuna_fallback_used,
        });
      } else {
        setSemanticInfo(null);
      }

      // Location-relax notice — fires when the user's ZIP/location filter
      // killed the response and we had to retry without it.
      if (res.data.location_relaxed && res.data.original_location && fetched.length > 0) {
        setLocationRelaxedInfo({
          original_location: res.data.original_location,
          level: res.data.location_relaxation_level, // 'state' | 'nationwide'
          relaxed_to: res.data.relaxed_to_location || '',
        });
      }
      // Kick off match scoring in parallel — non-blocking
      fetchMatches(fetched);
    } catch (err) {
      setSearchError('Failed to load jobs. Please try again.');
      logger.error('Job search failed:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, workType, zipCode, distance, salaryMin, salaryMax, fetchMatches]);

  const handleTailor = useCallback((job, match) => {
    try {
      trackEvent('job_match_card_click', {
        score: match?.score ?? null,
        score_bucket: match?.score == null ? 'no_score' : (match.score >= 80 ? 'strong' : match.score >= 60 ? 'good' : match.score >= 40 ? 'partial' : 'low'),
        source: job.source,
      });
    } catch (_e) { /* noop */ }
    navigate('/resume/new', {
      state: {
        fromJobBoard: true,
        title: `Resume for ${job.title} at ${job.company}`,
        target_job: job.title,
        target_company: job.company,
        job_description: job.description || '',
        job_url: job.url || job.apply_url || job.job_url || '',
      },
    });
  }, [navigate]);

  // Load jobs on first visit
  useEffect(() => {
    handleSearch('');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply sort. Newest = original API order. Best-match = score desc, then original order.
  const orderedJobs = useMemo(() => {
    if (sortBy !== 'best-match' || !Object.keys(matches).length) {
      return jobs.map((j, i) => ({ job: j, idx: i, key: keyFor(j, i) }));
    }
    return jobs
      .map((j, i) => ({ job: j, idx: i, key: keyFor(j, i) }))
      .sort((a, b) => {
        const sa = matches[a.key]?.score ?? -1;
        const sb = matches[b.key]?.score ?? -1;
        if (sb !== sa) return sb - sa;
        return a.idx - b.idx;
      });
  }, [jobs, matches, sortBy]);

  const hasScores = Object.keys(matches).length > 0;

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20" data-testid="job-board-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')} className="inline-flex items-center space-x-2 text-[#4b5563] hover:text-[#002fa7] mb-4" data-testid="back-button">
          <ArrowLeft size={20} /><span>{isAuthenticated ? 'Back to Dashboard' : 'Back to Home'}</span>
        </button>

        <div className="mb-8">
          <h1 className="font-satoshi text-3xl font-black tracking-tight text-[#0f1115]">Job Board</h1>
          <p className="text-[#4b5563] mt-1">Find jobs and instantly tailor your resume for each application</p>
        </div>

        {/* Search Bar */}
        <div className="dashboard-card p-4 mb-6 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b7280]" size={18} />
              <input
                type="text"
                placeholder="Search by job title, skill, or keyword..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                className="w-full pl-10 pr-4 py-3 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors"
                data-testid="job-search-input"
              />
            </div>
            <button
              onClick={() => handleSearch()}
              disabled={loading}
              className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-50"
              data-testid="search-jobs-btn"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              Search Jobs
            </button>
          </div>

          {/* Filters Row */}
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-[10px] uppercase tracking-wider font-bold text-[#6b7280] mb-1">Work Type</label>
              <div className="flex gap-1">
                {[
                  { value: 'all', label: 'All' },
                  { value: 'remote', label: 'Remote' },
                  { value: 'onsite', label: 'On-Site' },
                  { value: 'hybrid', label: 'Hybrid' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setWorkType(opt.value)}
                    className={`px-3 py-2 text-xs font-semibold transition-colors ${
                      workType === opt.value
                        ? 'bg-[#002fa7] text-white'
                        : 'bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] hover:text-[#002fa7]'
                    }`}
                    data-testid={`work-type-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-[#6b7280] mb-1">Zip Code</label>
              <input
                type="text"
                placeholder="e.g., 75001"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                maxLength={10}
                className="w-32 px-3 py-2 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors"
                data-testid="zip-code-input"
              />
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-[#6b7280] mb-1">Max Distance</label>
              <select
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                className="px-3 py-2 border border-[#e5e7eb] text-sm bg-white appearance-none cursor-pointer pr-8"
                data-testid="distance-select"
              >
                <option value="">Any</option>
                <option value="10">10 miles</option>
                <option value="25">25 miles</option>
                <option value="50">50 miles</option>
                <option value="75">75 miles</option>
                <option value="100">100 miles</option>
                <option value="150">150 miles</option>
                <option value="200">200 miles</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-[#6b7280] mb-1">Min Salary</label>
              <div className="relative">
                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6b7280]" size={14} />
                <input
                  type="number"
                  placeholder="e.g., 60000"
                  value={salaryMin}
                  onChange={(e) => setSalaryMin(e.target.value)}
                  className="w-32 pl-7 pr-3 py-2 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors"
                  data-testid="salary-min-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-[#6b7280] mb-1">Max Salary</label>
              <div className="relative">
                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6b7280]" size={14} />
                <input
                  type="number"
                  placeholder="e.g., 150000"
                  value={salaryMax}
                  onChange={(e) => setSalaryMax(e.target.value)}
                  className="w-32 pl-7 pr-3 py-2 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors"
                  data-testid="salary-max-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Match-context CTAs (logged out OR logged-in but no resume yet) */}
        {!loading && jobs.length > 0 && !isAuthenticated && (
          <div
            className="dashboard-card p-4 mb-4 bg-gradient-to-r from-[#002fa7] to-[#1e40af] text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            data-testid="job-match-signin-cta"
          >
            <div className="flex items-start gap-3">
              <Sparkles size={20} className="flex-shrink-0 mt-0.5 text-[#fbbf24]" />
              <div>
                <p className="font-satoshi font-bold text-sm">See how each job matches your resume</p>
                <p className="text-xs text-white/80 mt-0.5">Sign in (or run a free ATS check) and we'll score every job against your resume in real time.</p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Link to="/free-ats-check" className="bg-white text-[#002fa7] font-semibold text-xs px-3 py-2 hover:bg-[#fbbf24] transition-colors" data-testid="job-match-cta-free-check">
                Run free check
              </Link>
              <Link to="/login" className="border border-white text-white font-semibold text-xs px-3 py-2 hover:bg-white/10 transition-colors" data-testid="job-match-cta-login">
                Sign in
              </Link>
            </div>
          </div>
        )}
        {!loading && jobs.length > 0 && isAuthenticated && matchMeta?.reason === 'no_resume' && (
          <div
            className="dashboard-card p-4 mb-4 bg-[#fef3c7] border-l-4 border-[#f59e0b] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            data-testid="job-match-no-resume-cta"
          >
            <div>
              <p className="font-satoshi font-bold text-sm text-[#0f1115]">Add a resume to see match scores</p>
              <p className="text-xs text-[#7c2d12] mt-0.5">Once you have one resume, every job card will show how well it matches.</p>
            </div>
            <Link to="/resume/new" className="btn-primary text-xs px-3 py-2" data-testid="job-match-cta-create-resume">
              Create resume
            </Link>
          </div>
        )}

        {/* Results */}
        {searchError && (
          <div className="dashboard-card p-4 mb-4 bg-[#fee2e2] text-[#e11d48] text-sm" data-testid="search-error">
            {searchError}
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="dashboard-card p-5 animate-pulse">
                <div className="h-4 bg-[#e5e7eb] w-1/4 mb-3"></div>
                <div className="h-5 bg-[#e5e7eb] w-3/4 mb-2"></div>
                <div className="h-4 bg-[#e5e7eb] w-1/2 mb-4"></div>
                <div className="h-8 bg-[#e5e7eb] w-1/3"></div>
              </div>
            ))}
          </div>
        )}

        {!loading && searched && jobs.length === 0 && (
          <div className="dashboard-card p-12 text-center" data-testid="job-search-empty-state">
            <Search size={48} className="mx-auto mb-4 text-[#d1d5db]" />
            <p className="font-semibold text-[#0f1115]">No jobs found</p>
            <p className="text-sm text-[#6b7280] mt-1">
              Try a shorter keyword (e.g. "Identity Management" instead of full role title), remove the location/distance filter, or switch work type to "All".
            </p>
          </div>
        )}

        {!loading && jobs.length > 0 && (
          <>
            {broadenedInfo && (
              <div
                className="mb-4 px-4 py-3 rounded-lg border border-[#fde68a] bg-[#fffbeb] text-sm text-[#78350f]"
                data-testid="job-search-broadened-banner"
              >
                No exact matches for <strong>"{broadenedInfo.original}"</strong>. Showing results for{' '}
                <strong>"{broadenedInfo.used}"</strong> instead.
              </div>
            )}
            {locationRelaxedInfo && (
              <div
                className="mb-4 px-4 py-3 rounded-lg border border-[#fed7aa] bg-[#fff7ed] text-sm text-[#9a3412]"
                data-testid="job-search-location-relaxed-banner"
              >
                {locationRelaxedInfo.level === 'state' && locationRelaxedInfo.relaxed_to ? (
                  <>
                    No listings turned up exactly in <strong>"{locationRelaxedInfo.original_location}"</strong>, so
                    we widened to <strong>{locationRelaxedInfo.relaxed_to}</strong>-wide results.
                    Try a different ZIP or widen the distance to refine.
                  </>
                ) : (
                  <>
                    We didn't find listings near <strong>"{locationRelaxedInfo.original_location}"</strong> for this role,
                    so we're showing matches from anywhere. Try a different ZIP, a wider distance, or remove the location filter to refine.
                  </>
                )}
              </div>
            )}
            {semanticInfo && (
              <div
                className="mb-4 px-4 py-3 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] text-sm text-[#1e3a8a]"
                data-testid="job-search-semantic-banner"
              >
                We also searched related titles to show you the full market:{' '}
                {semanticInfo.variants.slice(1).map((v, i, arr) => (
                  <React.Fragment key={v}>
                    <strong>"{v}"</strong>{i < arr.length - 1 ? ', ' : ''}
                  </React.Fragment>
                ))}
                {semanticInfo.adzunaUsed && (
                  <span className="ml-1 text-[11px] text-[#1e40af]">· (added results from secondary board)</span>
                )}
              </div>
            )}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <p className="text-sm text-[#6b7280]">
                Showing <strong className="text-[#0f1115]">{jobs.length}</strong> of {total} jobs
                {hasScores && matchMeta?.resume_title && (
                  <span className="ml-2 text-[#9ca3af]" data-testid="match-against-label">
                    · matched against <strong className="text-[#0f1115]">{matchMeta.resume_title}</strong>
                  </span>
                )}
              </p>
              <div className="flex items-center gap-2">
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#6b7280]">Sort</label>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    try { trackEvent('job_match_sort_change', { sort: e.target.value }); } catch (_e) { /* noop */ }
                  }}
                  className="px-2 py-1.5 border border-[#e5e7eb] text-xs bg-white appearance-none cursor-pointer pr-7"
                  data-testid="job-sort-select"
                >
                  <option value="newest">Newest</option>
                  <option value="best-match" disabled={!hasScores}>{hasScores ? 'Best match' : 'Best match (sign in)'}</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-[#9ca3af] mb-4">
              <Tag size={12} /> Click "Tailor Resume" to create an optimized resume for any job
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {orderedJobs.map(({ job, key }) => (
                <JobCard
                  key={key}
                  job={job}
                  onTailor={handleTailor}
                  match={matches[key]}
                />
              ))}
            </div>
          </>
        )}

        {!loading && !searched && jobs.length === 0 && (
          <div className="dashboard-card p-12 text-center">
            <Briefcase size={48} className="mx-auto mb-4 text-[#d1d5db]" />
            <p className="font-semibold text-[#0f1115]">Loading jobs...</p>
          </div>
        )}
      </div>
    </div>
  );
}
